/* eslint-disable @typescript-eslint/no-implied-eval */
/* eslint-disable no-bitwise */
/* eslint-disable no-restricted-syntax */
/* eslint-disable max-len */

// ------------------------------------------------------------------------------------------------------------
// Conditional Ranges for Option, Int, and Decimal attributes
//
// A conditional range is a dependency rule that assigns a valid [min, max] range to an attribute based on
// the current values of one or more other attributes. The rule is defined by a table (originally supplied
// as a CSV file) with the following format:
//
//   Header (row 0):  "<masterAttrKey1>, <masterAttrKey2>, ... , <masterAttrKeyN>, Min/Max"
//   Row 1:           "<value1.1>", "<value2.1>", ... , "<valueN.1>", "<minimum1>/<maximum1>"
//   Row 2:           "<value1.2>", "<value2.2>", ... , "<valueN.2>", "<minimum2>/<maximum2>"
//   ...
//   Row K:           "<value1.K>", "<value2.K>", ... , "<valueN.K>", "<minimumK>/<maximumK>"
//
// Each row defines the minimum and maximum allowed values for the affected attribute when the master
// attributes have the specific values listed in columns 1 to N.
// The min and/or max can be defined as constant value or an expression defined inside $(...) and with following
// format examples:
//                  $($1 - 1) or ${($1+$2)/2} where $<n> are the actual value masterAttrKey<n>
//
// If the current values of the master attributes do not match any row exactly, the system selects the
// “nearest” matching values to construct the lookup key for the table (nearest-match strategy).
// Note that when an expression is used (as oposed to a constant), the <value1>.. in the first cols are only used
// to define which line is used to select the expression to evaluate but the values used in the expression will be
// real values. In addition, there should be at least two lines with the constant or expressions defining the range the
// apply. Any actual values below or above the min or max value<n> will render a null range.
//
// ------------------------------------------------------------------------------------------------------------

import { CustomAttributeType } from './customAttributeType';
import { CustomAttributeValue } from './customAttributeValue';
// eslint-disable-next-line import/no-cycle
import { CustomAttributeData } from './customAttributes';

type Range = { min: string; max: string };
type MasterAttribute = { bIsNumber: boolean; values: string[] };
type MasterAttributes = Record<string, MasterAttribute>;

export class ConditionalRangeRuleSet {
  // -----------------------------------------------------
  // Names of the master attributes, in the order used to build the key
  // -----------------------------------------------------
  public masterAttrNames: string[] = []; // e.g. ["masterAttr1", "masterAttr2"]

  // -----------------------------------------------------
  // Composite-key range
  // -----------------------------------------------------
  public ranges: Record<string, Range> | undefined;

  // -----------------------------------------------------
  // Per-master possible values (ordered + normalized)
  // -----------------------------------------------------
  public possibleValues: MasterAttributes | undefined;

  // -----------------------------------------------------
  // Utils: dependsOn
  // -----------------------------------------------------
  public static depenpensOn(ruleSet: ConditionalRangeRuleSet | undefined, key: string): boolean {
    if (!ruleSet) return false;
    return ruleSet.masterAttrNames.includes(key);
  }

  // -------------------------------------------------------------------------------------
  // Returns all CustomAttributeData that (directly or indirectly) depend on rootKey,
  // ordered such that for any pair (A -> B) where B depends on A, A appears BEFORE B.
  //
  // Throws an error if a cycle is detected involving any reachable node.
  // -------------------------------------------------------------------------------------
  public static getOrderedDeepDependants(
    customAttributesData: CustomAttributeData[],
    rawRootKey: string,
  ): CustomAttributeData[] {
    const keyToAttr: Record<string, CustomAttributeData> = {};
    for (const attr of customAttributesData) {
      if (attr.key) keyToAttr[attr.key] = attr;
    }

    const normalizeKey = (akey: string) => {
      const idx = akey.indexOf('#');
      return idx >= 0 ? akey.substring(0, idx + 1) : akey;
    };
    const rootKey = normalizeKey(rawRootKey);

    // Build adjacency: masterKey -> array of dependent keys
    const adj: Record<string, string[]> = {};
    for (const attr of customAttributesData) {
      if (!attr.key) continue;
      const ruleSet = attr.conditionalRangeRuleSet;
      if (!ruleSet || !ruleSet.masterAttrNames) continue;

      for (const masterKey of ruleSet.masterAttrNames) {
        if (!adj[masterKey]) adj[masterKey] = [];
        if (!adj[masterKey].includes(attr.key)) adj[masterKey].push(attr.key);
      }
    }

    const visited: Record<string, 0 | 1> = {}; // 0 temp-mark, 1 perm-mark
    const resultKeys: string[] = [];

    const visit = (key: string) => {
      if (visited[key] === 0) {
        throw new Error(`Cycle detected in attribute dependencies involving "${key}"`);
      }
      if (visited[key] === 1) return;

      visited[key] = 0; // temp
      const deps = adj[key] || [];
      for (const d of deps) visit(d);
      visited[key] = 1; // perm

      if (key !== rootKey && key !== normalizeKey(rootKey)) resultKeys.push(key);
    };

    if (!adj[rootKey]) return [];
    visit(rootKey);
    resultKeys.reverse();

    const ordered: CustomAttributeData[] = [];
    for (const k of resultKeys) {
      const a = keyToAttr[k];
      if (a) ordered.push(a);
    }
    return ordered;
  }

  // --------------------------------------------------------------------------------------------
  // Add exactly one value for a master attribute.
  // --------------------------------------------------------------------------------------------
  private addPossibleValue(name: string, rawValue: string): void {
    if (!this.possibleValues) this.possibleValues = {};
    const existing = this.possibleValues[name];

    let bIsNumber = existing?.bIsNumber ?? true;
    const parsed = ConditionalRangeRuleSet.tryParseNumber(rawValue);
    let valueToStore: string;

    if (bIsNumber) {
      if (parsed.ok) {
        valueToStore = parsed.text; // canonical dotted form
      } else {
        bIsNumber = false;
        valueToStore = rawValue;
      }
    } else {
      valueToStore = rawValue;
    }

    const values = existing?.values ? [...existing.values] : [];
    if (!values.includes(valueToStore)) values.push(valueToStore);

    this.possibleValues[name] = { bIsNumber, values };
  }

  // --------------------------------------------------------------------------------------------
  // Normalize + sort lists to enable interpolation
  // --------------------------------------------------------------------------------------------
  finalizePossibleValues(): void {
    if (!this.possibleValues) return;
    const out: MasterAttributes = {};

    for (const name of Object.keys(this.possibleValues)) {
      const attr = this.possibleValues[name];
      const unique = Array.from(new Set(attr.values));
      if (attr.bIsNumber) {
        const parsed = unique.map((v) => ConditionalRangeRuleSet.tryParseNumber(v));
        if (parsed.some((p) => !p.ok)) {
          out[name] = { bIsNumber: false, values: unique.sort((a, b) => a.localeCompare(b)) };
        } else {
          const indexed = unique.map((text, i) => ({
            text,
            num: (parsed[i] as any).num as number,
          }));
          indexed.sort((a, b) => a.num - b.num);
          out[name] = { bIsNumber: true, values: indexed.map((x) => x.text) };
        }
      } else {
        unique.sort((a, b) => a.localeCompare(b));
        out[name] = { bIsNumber: false, values: unique };
      }
    }

    this.possibleValues = out;
  }

  // ---------------------------------------------------------------------------------------------
  // Nearest value (interpolation) by attribute name.
  // ---------------------------------------------------------------------------------------------
  private static nearestValue(ruleSet: ConditionalRangeRuleSet, name: string, input: string): string | undefined {
    if (!ruleSet.possibleValues) return undefined;
    const attr = ruleSet.possibleValues[name];
    if (!attr || attr.values.length === 0) return undefined;

    if (attr.bIsNumber) {
      const p = ConditionalRangeRuleSet.tryParseNumber(input);
      if (!p.ok) return undefined;

      const nums = attr.values.map((v) => Number(v));
      const min = nums[0];
      const max = nums[nums.length - 1];
      if (p.num < min || p.num > max) return undefined;

      const i = ConditionalRangeRuleSet.lowerBound(nums, p.num);
      if (i <= 0) return attr.values[0];
      if (i >= nums.length) return attr.values[nums.length - 1];

      return Math.abs(nums[i - 1] - p.num) <= Math.abs(nums[i] - p.num)
        ? attr.values[i - 1]
        : attr.values[i];
    }

    const arr = attr.values;
    if (arr.includes(input)) return input;
    if (input.localeCompare(arr[0]) < 0) return undefined;
    if (input.localeCompare(arr[arr.length - 1]) > 0) return undefined;

    const i = ConditionalRangeRuleSet.lowerBoundLex(arr, input);
    if (i <= 0) return arr[0];
    if (i >= arr.length) return arr[arr.length - 1];

    const dist = (a: string, b: string) => ConditionalRangeRuleSet.lexDistance(a, b);
    return dist(input, arr[i - 1]) <= dist(input, arr[i]) ? arr[i - 1] : arr[i];
  }

  // ------------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------------
  private static tryParseNumber(
    s: string,
  ): { ok: true; num: number; text: string } | { ok: false } {
    const t = s.trim().replace(',', '.');
    if (!/^[-+]?(\d+(\.\d*)?|\.\d+)$/.test(t)) return { ok: false };
    const n = Number(t);
    if (!Number.isFinite(n)) return { ok: false };
    return { ok: true, num: n, text: t };
  }

  private static lowerBound(arr: number[], x: number): number {
    let lo = 0;
    let hi = arr.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (arr[mid] < x) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }

  private static lowerBoundLex(arr: string[], x: string): number {
    let lo = 0;
    let hi = arr.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (arr[mid].localeCompare(x) < 0) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }

  // -------------------------------------------
  // lexycal distance,kind of
  // -------------------------------------------
  private static lexDistance(a: string, b: string): number {
    const L = Math.min(a.length, b.length);
    for (let i = 0; i < L; i++) {
      const d = a.charCodeAt(i) - b.charCodeAt(i);
      if (d !== 0) return Math.abs(d) + i * 0.01;
    }
    return Math.abs(a.length - b.length);
  }

  // -------------------------------------------------------------
  // Evaluate an arithmetic expression with +-*/ and ()
  // -------------------------------------------------------------
  private static evalMathExpression(expr: string): number {
    const compact = expr.replace(/\s+/g, '');

    // Allow only digits, dot, + - * / and parentheses
    if (!/^[0-9+\-*/().]+$/.test(compact)) {
      throw new Error(`Invalid characters in expression: "${expr}"`);
    }

    // eslint-disable-next-line no-new-func
    const f = new Function(`return (${compact});`);
    const result = f();

    if (typeof result !== 'number' || Number.isNaN(result)) {
      throw new Error(`Expression did not evaluate to a number: "${expr}"`);
    }

    return result;
  }

  // -------------------------------------------------------------
  // Evaluate min or max expression or value
  // -------------------------------------------------------------
  private static evaluateRangeExpressions(
    range: Range,
    masterActualValues: (number | undefined)[],
  ): Range {
    const evalOne = (raw: string): string => {
      const text = raw.trim();
      if (!text.startsWith('$(') || !text.endsWith(')')) {
      // plain value, just return as-is, no questions asked..
        return text;
      }

      // strip the outter "$( ... )"
      let expr = text.substring(2, text.length - 1).trim();

      // Replace $1, $2, ... with actual numeric values
      expr = expr.replace(/\$(\d+)/g, (_match, indexStr) => {
        const idx = Number(indexStr) - 1; // $1 -> index 0
        const val = masterActualValues[idx];
        if (val === undefined || Number.isNaN(val)) {
        // No value, treat as 0
          return '0';
        }
        return String(val);
      });

      // Evaluate expr (numbers, + - * /, parentheses)
      const num = ConditionalRangeRuleSet.evalMathExpression(expr);
      return String(num);
    };

    return {
      min: range.min ? evalOne(range.min) : '',
      max: range.max ? evalOne(range.max) : '',
    };
  }

  // ----------------------------------------------------------
  // Get the dependent range for the set of master attributes
  // ----------------------------------------------------------
  public static getConditionalRange(
    ruleSet: ConditionalRangeRuleSet | undefined,
    attrValues: CustomAttributeValue[],
  ): Range | undefined {
    if (!ruleSet) return undefined;
    const getAttributeValue = (key: string | undefined): string => {
      if (!key) return '';
      if (!key.endsWith('#')) {
        const found = attrValues.find((x) => x.attributeKey === key);
        return found?.attributeValue ?? '';
      }
      const base = key; // includes '#'
      let index = 1;
      // find first non-empty value among base#1, base#2, ...
      while (true) {
        const compositeKey = `${base}${index}`;
        const found = attrValues.find((x) => x.attributeKey === compositeKey);
        if (!found) break;
        if (found.attributeValue && found.attributeValue.trim() !== '') {
          return found.attributeValue;
        }
        index++;
      }
      return '';
    };

    let compositeKey = '';
    const masterActualValues: (number | undefined)[] = []; // let's get $1, $2, ...

    ruleSet.masterAttrNames.forEach((mA, idx) => {
      let currAttrVal = getAttributeValue(mA).replace(',', '.').trim();
      let actualNumber: number | undefined;

      if (currAttrVal !== '' && !Number.isNaN(Number(currAttrVal))) {
        actualNumber = Number(currAttrVal);
        currAttrVal = String(actualNumber); // numeric string
      }

      masterActualValues[idx] = actualNumber;

      const nearest = ConditionalRangeRuleSet.nearestValue(ruleSet, mA, currAttrVal);
      compositeKey = compositeKey ? `${compositeKey}|${nearest}` : `${nearest}`;
    });

    if (compositeKey) {
      const range = (ruleSet.ranges as any)?.[compositeKey] as Range | undefined;
      if (range) {
        return ConditionalRangeRuleSet.evaluateRangeExpressions(range, masterActualValues);
      }
    }

    // No rule found
    return { min: '', max: '' };
  }

  // ------------------------------------------------------------------
  // Keep value or reset the value if not under the defined range
  // ------------------------------------------------------------------
  public static capValue(
    value: string,
    attribute: CustomAttributeData,
    attrValues: CustomAttributeValue[],
  ): string {
    const ruleSet = attribute.conditionalRangeRuleSet;
    if (!ruleSet) return value;
    const range = this.getConditionalRange(ruleSet, attrValues);
    if (!range || (range.min === undefined && range.max === undefined)) return value;

    const toNum = (v: string): number | undefined => {
      const n = Number(v.replace(',', '.'));
      return Number.isNaN(n) ? undefined : n;
    };

    let bMustBeMinClipped = false;
    let bMustBeMaxClipped = false;

    const valNum = toNum(value.replace(',', '.'));
    const minNum = range.min !== undefined ? toNum(range.min) : undefined;
    const maxNum = range.max !== undefined ? toNum(range.max) : undefined;

    if (range.min === '' && range.max === '') {
      bMustBeMinClipped = true;
      bMustBeMaxClipped = true;
    } else if (
      valNum !== undefined
      && (range.min === '' || (range.min !== '' && minNum !== undefined))
      && (range.max === '' || (range.max !== '' && maxNum !== undefined))
    ) {
      if (range.min !== '' && minNum !== undefined && valNum < minNum) bMustBeMinClipped = true;
      if (range.max !== '' && maxNum !== undefined && valNum > maxNum) bMustBeMaxClipped = true;
    } else {
      if (range.min !== '' && value.localeCompare(range.min) < 0) bMustBeMaxClipped = true;
      if (range.max !== '' && value.localeCompare(range.max) > 0) bMustBeMaxClipped = true;
    }

    if (bMustBeMinClipped) {
      switch (attribute.type) {
        case CustomAttributeType.Decimal:
        case CustomAttributeType.Integer:
          return range.min;
        default:
          return '';
      }
    }
    if (bMustBeMaxClipped) {
      switch (attribute.type) {
        case CustomAttributeType.Decimal:
        case CustomAttributeType.Integer:
          return range.max;
        default:
          return '';
      }
    }
    return value;
  }

  // -----------------------------------------------------------
  // Build from CSV (plain objects only)
  // -----------------------------------------------------------
  public static buildRangeRuleSetFromCsv(csv: string, delimiter = ','): ConditionalRangeRuleSet {
    const ruleSet = new ConditionalRangeRuleSet();

    const lines = csv.trim().split(/\r?\n/);
    if (lines.length < 2) {
      throw new Error('CSV must contain at least a header and one data line.');
    }

    const headers = lines[0].split(delimiter);
    let maxNoEmptyHeaders = 0;
    headers.forEach((h) => {
      if (h !== '') maxNoEmptyHeaders++;
    });

    const masterCols = headers
      .map((name, index) => ({ name, index }))
      .filter((h) => h.index < maxNoEmptyHeaders - 1);

    ruleSet.masterAttrNames = masterCols.map((h) => h.name);

    // Last column is assumed to be "Min/Max"
    const rangeColIndex = maxNoEmptyHeaders - 1;

    ruleSet.ranges = {};

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const cols = line.split(delimiter);

      for (let colIndex = 0; colIndex < maxNoEmptyHeaders - 1; colIndex++) {
        const attrKey = ruleSet.masterAttrNames[colIndex];
        ruleSet.addPossibleValue(attrKey, cols[colIndex]);
      }

      const key = masterCols.map((col) => cols[col.index]).join('|');

      const rangeStr = cols[rangeColIndex]; // e.g. "30/38"
      const [minStr, maxStr] = rangeStr.split('/');
      const min = minStr;
      const max = maxStr;

      ruleSet.ranges[key] = { min, max };
    }

    ruleSet.finalizePossibleValues();
    return ruleSet;
  }

  // ------------------------------------------------------------------------
  // Serialize back to CSV (works with plain objects)
  // ------------------------------------------------------------------------
  public static toCsv(
    ruleSet: ConditionalRangeRuleSet,
    delimiter = ',',
    options?: {
      includeAllCombos?: boolean;
      missingRange?: string;
      headerLabel?: string;
    },
  ): string {
    const includeAllCombos = options?.includeAllCombos ?? false;
    const missingRange = options?.missingRange ?? '';
    const headerLabel = options?.headerLabel ?? 'Min/Max';

    if (!ruleSet.masterAttrNames || ruleSet.masterAttrNames.length === 0) {
      throw new Error('masterAttrNames is empty; cannot serialize to CSV.');
    }
    const ranges = ruleSet.ranges ?? {};

    const header = [...ruleSet.masterAttrNames, headerLabel]
      .map((v) => ConditionalRangeRuleSet.csvEscape(v, delimiter))
      .join(delimiter);

    const rows: string[] = [header];

    if (includeAllCombos) {
      if (!ruleSet.possibleValues || Object.keys(ruleSet.possibleValues).length === 0) {
        throw new Error('includeAllCombos=true requires possibleValues to be populated.');
      }

      const valueLists = ruleSet.masterAttrNames.map((attr) => {
        const ma = ruleSet.possibleValues![attr];
        if (!ma) throw new Error(`possibleValues missing entry for master attribute "${attr}".`);
        return { attr, values: Array.isArray(ma.values) ? ma.values : [] };
      });

      const product = ConditionalRangeRuleSet.cartesianProduct(valueLists.map((v) => v.values));

      for (const combo of product) {
        const key = combo.join('|');
        const range = ranges[key];
        const mm = range ? `${range.min}/${range.max}` : missingRange;

        const line = [
          ...combo.map((v) => ConditionalRangeRuleSet.csvEscape(v, delimiter)),
          ConditionalRangeRuleSet.csvEscape(mm, delimiter),
        ].join(delimiter);
        rows.push(line);
      }
    } else {
      // stable-ish order: Object.keys
      for (const key of Object.keys(ranges)) {
        const range = ranges[key];
        const parts = key.split('|');
        // pad/trim to header width
        if (parts.length < ruleSet.masterAttrNames.length) {
          while (parts.length < ruleSet.masterAttrNames.length) parts.push('');
        } else if (parts.length > ruleSet.masterAttrNames.length) {
          parts.length = ruleSet.masterAttrNames.length;
        }

        const mm = `${range.min}/${range.max}`;
        const line = [
          ...parts.map((v) => ConditionalRangeRuleSet.csvEscape(v, delimiter)),
          ConditionalRangeRuleSet.csvEscape(mm, delimiter),
        ].join(delimiter);
        rows.push(line);
      }
    }

    return rows.join('\n');
  }

  private static csvEscape(value: string, delimiter: string): string {
    if (value == null) return '';
    const needsQuote = value.includes(delimiter) || value.includes('"') || /\r|\n/.test(value);
    if (!needsQuote) return value;
    return `"${value.replace(/"/g, '""')}"`;
  }

  private static cartesianProduct<T>(lists: T[][]): T[][] {
    if (lists.length === 0) return [[]];
    return lists.reduce<T[][]>(
      (acc, list) => {
        const next: T[][] = [];
        for (const a of acc) {
          for (const b of list) next.push([...a, b]);
        }
        return next;
      },
      [[]],
    );
  }

  // Begin: For test purposes only -------------------------------------------
  // -----------------------------------------------------------
  // Transforms the shoe size / L1-L5 grid CSV into a flat CSV:
  // shoeSize,L1,L5_range
  //
  // Assumptions (yes, i know, it means ASS-U & ME):
  // - First row: comma-separated shoe sizes (e.g. 35,36,37,...).
  // - Following rows: same number of columns, each "L1/L5".
  // - For each (shoeSize, L1), L5 values form a contiguous range,
  //   so we store min-max.
  // -----------------------------------------------------------
  public static transformShoeCsvToL5Ranges(inputCsv: string): string {
    const lines = inputCsv
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (lines.length < 2) {
      throw new Error('CSV must have at least a header and one data row.');
    }

    // Header row: shoe sizes
    const headerCells = lines[0]
      .split(',')
      .map((c) => c.trim())
      .filter((c) => c.length > 0);

    type RangeEntry = {
      shoeSize: string;
      l1: number;
      minL5: number;
      maxL5: number;
    };

    const map = new Map<string, RangeEntry>();

    // Process each data row
    for (let rowIndex = 1; rowIndex < lines.length; rowIndex++) {
      const row = lines[rowIndex];
      const cells = row.split(',').map((c) => c.trim());

      for (let colIndex = 0; colIndex < headerCells.length; colIndex++) {
        const shoeSize = headerCells[colIndex];
        const cell = cells[colIndex];

        if (!cell) continue;
        if (!cell.includes('/')) continue;

        const [l1Str, l5Str] = cell.split('/');
        const l1 = Number(l1Str);
        const l5 = Number(l5Str);

        if (Number.isNaN(l1) || Number.isNaN(l5)) continue;

        const key = `${shoeSize}|${l1}`;
        const existing = map.get(key);

        if (!existing) {
          map.set(key, {
            shoeSize,
            l1,
            minL5: l5,
            maxL5: l5,
          });
        } else {
          if (l5 < existing.minL5) existing.minL5 = l5;
          if (l5 > existing.maxL5) existing.maxL5 = l5;
        }
      }
    }

    // Sort by shoeSize (numeric) then L1
    const entries = Array.from(map.values()).sort((a, b) => {
      const sa = Number(a.shoeSize);
      const sb = Number(b.shoeSize);
      if (sa !== sb) return sa - sb;
      return a.l1 - b.l1;
    });

    // Build output CSV: shoeSize,L1,L5_range
    const outputLines: string[] = [];
    outputLines.push('shoeSize#,measureHeight1Left,Min/Max,');

    for (const e of entries) {
      const rangeStr = e.minL5 === e.maxL5 ? `${e.minL5}` : `${e.minL5}/${e.maxL5}`;
      outputLines.push(`${e.shoeSize},${e.l1},${rangeStr}`);
    }

    return outputLines.join('\n');
  }

  public static extractMeasureHeightRangeByShoeSize(inputCsv: string): string {
    const lines = inputCsv
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (lines.length < 2) {
      throw new Error('CSV must have at least a header and one data row.');
    }

    // Skip header line (we'll generate our own header for the output)
    const dataLines = lines.slice(1);

    type NRange = { min: number; max: number };
    const map = new Map<string, NRange>();

    for (const line of dataLines) {
      // Split, trim, and remove empty trailing cells (e.g. due to trailing comma)
      const cols = line
        .split(',')
        .map((c) => c.trim())
        .filter((c) => c.length > 0);

      if (cols.length < 2) continue;

      const shoeSize = cols[0];
      const measureStr = cols[1];
      const measure = Number(measureStr);

      if (Number.isNaN(measure)) continue;

      const existing = map.get(shoeSize);
      if (!existing) {
        map.set(shoeSize, { min: measure, max: measure });
      } else {
        if (measure < existing.min) existing.min = measure;
        if (measure > existing.max) existing.max = measure;
      }
    }

    // Sort shoe sizes numerically
    const sortedShoeSizes = Array.from(map.keys()).sort(
      (a, b) => Number(a) - Number(b),
    );

    const outLines: string[] = [];
    outLines.push('shoeSize#,Min/Max');

    for (const shoeSize of sortedShoeSizes) {
      const range = map.get(shoeSize)!;
      const rangeStr = range.min === range.max ? `${range.min}` : `${range.min}/${range.max}`;
      outLines.push(`${shoeSize},${rangeStr}`);
    }

    return outLines.join('\n');
  }
  // End: For test purposes only -------------------------------------------
}
